/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Tue May 02 2023
 **/

export const APIErrorCode = {
	UNKNOWN: "49999",
	DATA_NOT_FOUND: "49001",
	PARAMETER_NOT_VALID: "49002",
	EMAIL_SEND_FAIL: "49003",
	UNAUTHORIZED_ACCESS: "49004",
	UPLOAD_FAIL: "49005",
	NOT_SUPPORTED_LANGUAGE: "49006",
	NOT_SUPPORTED_FILE_TYPE: "49007",
	EXTERNAL_API_CALL_FAILED: "49008",
	FAILED_LOCK_SYNCHRONIZED: "49009",
	INVALID_FILE: "49010",

	//group
	DUPLICATE_GROUP_NAME: "49100",
	ALREADY_JOINED_OR_JOIN_REQUESTED_GROUP: "49101",
	NOT_JOINED_GROUP: "49102",
	FAILED_CANCEL_GROUP_JOIN_REQUEST: "49103",
	GROUP_DELETE_FAILED: "49104",
	GROUP_USER_DELETE_FAILED_BY_ADMIN: "49105",
	GROUP_USER_DELETE_FAILED_BY_ADMIN_NEED_ASSIGN_OTHER_SPACE_ADMIN: "49106",
	GROUP_USER_DELETE_FAILED_BY_USER_NEED_ASSIGN_OTHER_SPACE_ADMIN: "49107",
	GROUP_USER_DELETE_FAILED_BY_ADMIN_IS_LAST_GROUP_USER: "49108",
	GROUP_USER_DELETE_FAILED_BY_ADMIN_IS_SINGLE_GROUP_ADMIN: "49109",
	GROUP_INVITE_FAILED_DUPLICATED_GROUP_USERNAME: "49110",
	GROUP_JOIN_FAILED: "49111",
	GROUP_INVITE_RETRY_FAILED: "49112",
	GROUP_ADMIN_CHANGE_FAILED: "49113",

	//user
	USERNAME_OR_PASSWORD_NOT_MATCHED: "49200",
	USER_ACCOUNT_DISABLED: "49201",
	USER_ACCOUNT_LOCKED: "49202",
	DUPLICATE_USERNAME: "49203",
	ALREADY_WITHDRAWN_USER: "49204",
	ALREADY_AUTHENTICATED: "49205",
	PASSWORD_UPDATE_DATE_NOT_EXPIRED: "49206",
	EMAIL_AUTHENTICATION_FAILED: "49207",
	INCORRECT_AUTHENTICATION_CODE: "49208",
	RESEND_CODE_AUTHENTICATION_FAILED: "49209",
	FAILED_OAUTH_AUTHENTICATION: "49210",
	FAILED_CONNECT_TO_OAUTH: "49211",
	ALREADY_FOLLOW: "49212",
	FAILED_UNFOLLOW: "49213",
	FAILED_CONTACT_BOOKMARK_DELETE: "49214",
	FAILED_USER_STATUS_CLEAR: "49215",
	NOT_ALLOWED_DOMAIN: "49216",
	INVALID_OAUTH_TOKEN: "49217",
	ALREADY_SIGNUP: "49226",
	LOGIN_WITH_OTHER_SOCIAL_ACCOUNT: "49228",
	LOGIN_WITH_EMAIL: "49229",
	ALREADY_LOGOUT_DEVICE: "49231",

	//space
	SPACE_DELETE_FAILED: "49300",
	SPACE_DELETE_CANCEL_FAILED: "49301",
	NOT_JOINED_SPACE: "49302",
	ALREADY_JOINED_OR_JOIN_REQUESTED_SPACE: "49303",
	FAILED_TO_ACCESS_PRIVATE_SPACE: "49304",
	SPACE_PARTICIPANTS_COUNT_EXCEEDED: "49305",
	NOT_PARTICIPATED_SPACE: "49306",
	ALREADY_PARTICIPATED_SPACE: "49307",
	CHANGE_NOTIFICATION_FAILED: "49308",
	SPACE_ADMIN_CHANGE_FAILED: "49309",
	FAILED_BLOCK_USER: "49310",
	SPACE_JOIN_FAILED: "49311",
	SPACE_INVITE_FAILED_DUPLICATED_USERNAME: "49312",
	SPACE_INVITE_RETRY_FAILED: "49313",
	SPACE_USER_DELETE_FAILED: "49314",
	FAILED_REGISTER_EXTERNAL_GROUP_USER_TO_GROUP: "49315",
	NOT_ALLOWED_SPACE_ACCESS_ALLOW_TYPE: "49316",
	NO_SPACE_PARTICIPATION_AUTHORITY: "49325",
	NOT_ALLOWED_IP: "49115",
	SPACE_NOT_ALLOWED_OTHER_GROUP_USER: "49324",
	UNAUTHORIZED_ACCESS_BY_GUEST: "49233",

	// chat
	EXIST_SAME_CHAT_MEMBER: "48101",
	NOT_ALLOWED_DM_INVITE: "49400",

	// people
	FRIEND_REQUEST_FAILED: "49223",
	FAILED_FOLLOW_SPACE_BY_NONE_AUTHORITY: "49319",
	FAILED_FOLLOW_SPACE: "49321",

	// call
	RECEIVER_ACTIVE_CALL_EXISTS: "49700",

	// group call
	CALL_USER_MAX_COUNT_EXCEEDED: "49701",
	ALL_RECEIVER_BUSY: "49703",
	EXCEED_MAX_CALL_PER_ACCOUNT: "49706",

	// conference
	FAILED_FOLLOW_SPACE_BY_NONE_CONFERENCE: "44901",
	FAILED_FOLLOW_CONFERENCE_BY_NONE_SAME_GROUP: "49332",
	FAILED_FOLLOW_CONFERENCE_BECAUSE_MEETING_PRIVATE: "49331"
} as const;

export const ChatErrorCode = {
	UNKNOWN: "48999",
	DATA_NOT_FOUND: "48001",
	PARAMETER_NOT_VALID: "48002",
	UNAUTHORIZED_ACCESS: "48004",
	INVALID_USER_INVITE: "48100",
	EXIST_SAME_CHAT_MEMBER: "48101",
	NOT_JOINED_ROOM: "48102",
	NOT_JOINED_VOICE_CALL: "48103",
	ALREADY_JOINED_VOICE_CALL: "48104",
	ALREADY_RECORDING_VOICE_CALL: "48105",
	EMERGENCY_MESSAGE_NOT_ALLOWED: "48106"
} as const;

export const WebsocketErrorCode = {
	UNKNOWN: "47999",
	DESTINATION_NOT_ALLOWED: "47000",
	MESSAGE_TYPE_NOT_ALLOWED: "47001",
	JWT_VERIFICATION_FAILED: "47002",
	JWT_EXPIRED: "47003",
	WEBSOCKET_CONNECT_REQUIRED_HEADER_MISSING: "47004",
	NOT_JOINED_ROOM: "47005",
	SERVER_TO_SERVER_FAILED: "47006",
	INVALID_CONNECT_WITHOUT_LOGIN: "47007"
} as const;

export const FileErrorCode = {
	UNKNOWN: "46999",
	BAD_REQUEST: "46000",
	DATA_NOT_FOUND: "46001",
	PARAMETER_NOT_VALID: "46002",
	UNAUTHORIZED_ACCESS: "46003",
	NOT_ALLOWED_FOLDER_DEPTH: "46100",
	CANNOT_MOVE_TO_SUBFOLDER: "46101",
	DUPLICATE_FILE_NAME: "46102",
	NOT_ALLOWED_FILE: "46103",
	NOT_ALLOWED_LINK: "46104",
	EXCEED_MAX_FOLDER_COUNT: "46105",
	FILE_NAME_GREATER_50: "46110",
	FAILED_DOCUMENT_CONVERSION: "46111",
	NOT_SUPPORTED_FILE_TYPE: "46113"
} as const;

export const MeetingErrorCode = {
	JWT_VERIFICATION_FAILED: "44002"
};

export type APIErrorCode = (typeof APIErrorCode)[keyof typeof APIErrorCode];
export type ChatErrorCode = (typeof ChatErrorCode)[keyof typeof ChatErrorCode];
export type WebsocketErrorCode = (typeof WebsocketErrorCode)[keyof typeof WebsocketErrorCode];
export type FileErrorCode = (typeof FileErrorCode)[keyof typeof FileErrorCode];
export type MeetingErrorCode = (typeof MeetingErrorCode)[keyof typeof MeetingErrorCode];
